import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/api";
import { Backdrop, CircularProgress } from "@mui/material";
import ToastMessage from "../../utils/mui/righttoptoast";

export default function PurposeOfVisitModal({
  open,
  setFetchedData,
  setPurposeModalOpen,
  selectedCard,
  // Optional: full list of appointments to edit. Falls back to the single card.
  appointments,
  formatTime = (t) => t || "",
  // Which appointment (by position in `appointments`) to focus when opening.
  initialIndex = 0,
  onClose = () => {},
}) {
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const practiseid = window.getPracticeId();
  const [alertmessage, setAlertMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [toastopen, setOpenToast] = useState(false);

  // Build the working list of appointments. Each entry must resolve an
  // appointmentid (app_id) and id (cid). We fall back to selectedCard's IDs
  // when an allappts item doesn't carry its own.
  const apptList = useMemo(() => {
    const list =
      Array.isArray(appointments) && appointments.length
        ? appointments
        : selectedCard
          ? [selectedCard]
          : [];
    return list.map((appt, index) => ({
      key: appt?.appointmentid ?? appt?.id ?? index,
      index, // position inside data.allappts — used for the local patch
      appointmentid: appt?.appointmentid ?? selectedCard?.appointmentid,
      cid: appt?.id ?? selectedCard?.id,
      purpose: appt?.purpose || "",
      starttime: appt?.starttime,
      startdate: appt?.startdate ?? selectedCard?.startdate,
      visit_confirmation: appt?.visit_confirmation,
    }));
  }, [appointments, selectedCard]);

  // selectedByKey: { [appt.key]: optionObject } — one chosen purpose per appt
  const [selectedByKey, setSelectedByKey] = useState({});
  // which appointment row is currently being edited (defaults to first)
  const [activeKey, setActiveKey] = useState(null);

  const handlealert = (event, reason) => {
    if (reason === "clickaway") return;
    setOpenToast(false);
  };

  const groupedOptions = useMemo(() => {
    const groups = {};
    statusOptions.forEach((item) => {
      if (!groups[item.category]) groups[item.category] = [];
      groups[item.category].push(item);
    });
    return groups;
  }, [statusOptions]);

  const toDateStr = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    return (
      dt.getFullYear() +
      "-" +
      (dt.getMonth() + 1).toString().padStart(2, "0") +
      "-" +
      dt.getDate().toString().padStart(2, "0")
    );
  };

  const toggle = (item) => {
    if (activeKey == null) return;
    setSelectedByKey((prev) => ({ ...prev, [activeKey]: item }));
  };

  const fetchStatusData = async () => {
    setLoading(true);
    try {
      const response = await api.post(
        `/v1/${practiseid}/appointments/status-types`,
        { appointment_types: ["purpose"] },
      );
      if (response.status === 200) {
        setStatusOptions(response.data);

        // Pre-select each appointment's current purpose
        const initial = {};
        apptList.forEach((appt) => {
          if (appt.purpose) {
            const matched = response.data.find((p) => p.value == appt.purpose);
            if (matched) initial[appt.key] = matched;
          }
        });
        setSelectedByKey(initial);
        // Focus the requested appointment (falls back to the first).
        if (apptList.length) {
          const target =
            apptList.find((a) => a.index === initialIndex) || apptList[0];
          setActiveKey(target.key);
        }
      } else {
        setStatusOptions([]);
      }
    } catch (error) {
      console.error(error);
      setStatusOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchStatusData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const UpdateVisitPurpose = async () => {
    // Collect every appointment that has a chosen purpose.
    const changes = apptList
      .map((appt) => ({ appt, option: selectedByKey[appt.key] }))
      .filter(
        (c) => c.option && c.appt.appointmentid != null && c.appt.cid != null,
      );

    if (!changes.length) return;

    try {
      setIsLoading(true);
      const response = await api.put(
        `/v1/${practiseid}/appointments/visit-purpose/update`,
        {
          itemVal: changes.map((c) => c.option.value),
          dtype: changes.map(() => "dtpurpose"),
          app_id: changes.map((c) => c.appt.appointmentid),
          cid: changes.map((c) => c.appt.cid),
          // Keep a single searchdate (the card's date) to match prior behavior.
          searchdate: toDateStr(selectedCard?.startdate),
        },
      );

      if (response.status === 200) {
        setAlertMessage("Purpose Updated successfully");
        setSeverity("success");
        setOpenToast(true);

        // Reflect changes locally WITHOUT a dashboard refetch (a refetch would
        // drop the active search filter and reload a different card).
        // We patch by position (index) because the modal's appointment list is
        // built in the same order as data.allappts — this works even when
        // allappts entries don't carry their own id.
        const valueByIndex = {};
        changes.forEach((c) => {
          valueByIndex[c.appt.index] = c.option.value;
        });

        setFetchedData((prevData) =>
          prevData.map((item) => {
            if (item.id !== selectedCard.id) return item;
            const updated = { ...item };

            // Patch each appointment inside allappts by its position.
            if (Array.isArray(item.allappts)) {
              updated.allappts = item.allappts.map((a, idx) =>
                valueByIndex[idx] != null
                  ? { ...a, purpose: valueByIndex[idx] }
                  : a,
              );
            }

            // The card header shows `data.purpose` for the first slot, which
            // corresponds to the first appointment — keep it in sync.
            if (valueByIndex[0] != null) {
              updated.purpose = valueByIndex[0];
            }

            return updated;
          }),
        );

        setTimeout(() => {
          setOpenToast(false);
          setPurposeModalOpen(false);
        }, 1200);
      } else {
        setAlertMessage("Failed to Update Purpose. Please try again.");
        setSeverity("info");
        setOpenToast(true);
      }
    } catch (error) {
      console.error("Error updating visit purpose:", error);
      setAlertMessage("Something Went Wrong");
      setSeverity("error");
      setOpenToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  const showApptTabs = apptList.length > 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center pt-5">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative bg-white w-[90%] max-w-[720px] rounded-2xl shadow-xl overflow-hidden">
        <div className="px-3 py-2 pt-3 border-b flex items-center justify-between">
          <h3 className="text-[18px] font-semibold text-[#111827]">
            Purpose of visit
          </h3>
          <button onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} color="#9CA3AF" size="lg" />
          </button>
        </div>

        {/* Appointment selector (only when more than one appointment) */}
        {showApptTabs && (
          <div className="px-4 pt-3">
            <div className="text-xs text-left text-[#6B7280] font-inter mb-2">
              Select an appointment to set its type of visit
            </div>
            <div className="flex flex-wrap gap-2">
              {apptList.map((appt, idx) => {
                const isActive = activeKey === appt.key;
                const chosen = selectedByKey[appt.key];
                return (
                  <button
                    key={appt.key}
                    type="button"
                    onClick={() => setActiveKey(appt.key)}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-inter border text-left ${
                      isActive
                        ? "border-[#E88D67] bg-[#FDF1EB] text-[#9A3412]"
                        : "border-[#E5E7EB] bg-white text-[#1F2937] hover:bg-[#F9FAFB]"
                    }`}
                    title={chosen?.value || appt.purpose}
                  >
                    <span className="font-semibold">
                      Appt {idx + 1}
                      {appt.starttime ? ` · ${formatTime(appt.starttime)}` : ""}
                    </span>
                    <span className="block text-[11px] text-[#6B7280] truncate max-w-[160px]">
                      {chosen?.value || appt.purpose || "Not set"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="py-3 px-4 max-h-[55vh] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <CircularProgress size={28} sx={{ color: "#E88D67" }} />
              <span className="text-sm text-[#6B7280] font-inter">
                Loading options...
              </span>
            </div>
          ) : (
          Object.entries(groupedOptions).map(([category, items]) => (
            <div key={category} className="mb-3">
              <div className="text-sm text-left font-medium text-[#1F2937] font-inter mb-2">
                {`${category}`}
              </div>
              <div className="flex flex-wrap gap-3">
                {items.map((item) => {
                  const active =
                    activeKey != null &&
                    selectedByKey[activeKey]?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggle(item)}
                      className={`px-4 py-2 rounded-full text-[12px] font-[500] select-none font-inter ${
                        active
                          ? "bg-[#E88D67] text-white border-[#E88D67] shadow-md shadow-[#E1710033]"
                          : "bg-white text-[#1F2937] border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                      }`}
                      title={item.value}
                    >
                      {item.value}
                    </button>
                  );
                })}
              </div>
            </div>
          ))
          )}
        </div>

        <div className="px-5 py-3 border-t border-[#F3F4F6] flex items-center justify-between bg-[#F9FAFB] gap-5">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-lg bg-white border border-[#E5E7EB] font-inter text-[#1F2937] text-sm"
          >
            Cancel
          </button>
          <button
            onClick={UpdateVisitPurpose}
            className="w-full py-2.5 rounded-lg bg-[#111827] text-white font-inter text-sm disabled:opacity-50"
            disabled={Object.keys(selectedByKey).length === 0}
          >
            Update
          </button>
        </div>
      </div>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <ToastMessage
        message={alertmessage}
        severity={severity}
        open={toastopen}
        handleClose={handlealert}
      />
    </div>
  );
}
